/**
 * Row types for the `web.*` read plane served by PostgREST.
 *
 * These mirror, one-for-one, the views shipped by the mod's own migrations:
 *   database/src/main/resources/dbmigration/{postgres,h2}/1.4__web-read-views.sql
 *   database/src/main/resources/dbmigration/{postgres,h2}/1.5__web-logout-placement-view.sql
 *   database/src/main/resources/dbmigration/{postgres,h2}/1.6__display-item-as-spec.sql
 *   database/src/main/resources/dbmigration/{postgres,h2}/1.8__web-outbox-and-audit-views.sql
 *   database/src/main/resources/dbmigration/{postgres,h2}/1.38__web-staff-audit-views.sql
 *
 * THE SQL IS THE SOURCE OF TRUTH. When a view changes, change it there first and mirror it
 * here; nothing generates this file. Naming a view once and getting everything else is
 * split across two exports at the bottom, because half of it cannot survive to runtime:
 * {@link WEB_VIEW_NAMES} is the runtime list (and the REST path, one segment per name),
 * {@link WebViewRowMap} is the compile-time name-to-row-type map.
 *
 * Conventions inherited from 1.4, relied on by every consumer:
 *   - the first three columns are always `player_uuid`, `server_id`, `last_known_name`,
 *     so any row can be keyed to a player and rendered with their name without a second
 *     lookup;
 *   - `server_id` is a real column on the three views 1.8 added (an intent is claimed by
 *     exactly one server) and 1.4's stamped literal 'default' everywhere else. That swap is
 *     what the shape was designed for: it happened with no API and no frontend change;
 *   - `last_known_name` is null for a profile minted by an offline grant
 *     (PlayerProfileRepository.findOrCreate) for a player who has never joined.
 *
 * Wire-format notes: PostgREST renders `timestamptz` as an ISO-8601 string and `uuid` as a
 * string, so both are `string` here. Surrogate `id` columns are Ebean `Long`, which JSON
 * carries as a number - fine, they are far below 2^53.
 */

/** ISO-8601 instant as PostgREST renders it, e.g. "2026-08-01T15:26:00.123456+00:00". */
export type IsoInstant = string;

/** A UUID in canonical hyphenated text form. */
export type UuidText = string;

/** The three leading columns every `web.player_*` view starts with. */
export interface WebPlayerRef {
  readonly player_uuid: UuidText;
  readonly server_id: string;
  readonly last_known_name: string | null;
}

/** `web.player_directory` - name -> UUID search, the inspector's entry point. */
export interface PlayerDirectoryRow extends WebPlayerRef {
  readonly last_seen: IsoInstant | null;
  readonly created_at: IsoInstant;
  /**
   * The Discord snowflake this player has linked, or `null` when they have not (migration 1.10).
   *
   * NOT the first Discord snowflake in the read plane, and this column is not when one arrived.
   * `outboxActor` (./grants.ts) has written `discord:<snowflake> (name)` into `web_outbox.actor`
   * since the reward-grant path landed, the mod copies it onto `web_audit.actor`, and migration 1.8
   * exposes that column through both `web.outbox_status` and `web.audit_log`. A staff member's
   * snowflake was already at rest and already readable here before this column existed.
   *
   * What this run adds is a durable, queryable BINDING — snowflake ⇔ player_uuid — on THREE
   * surfaces, not one: this column (migration 1.10), which turns player_uuid ⇒ real-world account
   * into one indexed lookup rather than a string parsed out of an action record;
   * `web.audit_log.payload`, whose link body is `{discordId}`; and `web.outbox_status.payload`,
   * whose link intent body is `{linkCode, discordId}`. All three views are in the allow-list below,
   * and `POSTGRES_READ_ROLE_FOR` maps viewer, editor AND admin onto `web_read`, so every signed-in
   * staff member reads all three. See migration 1.9's header for the full statement.
   *
   * A `player`-tier token reads this column too, through `web_self.player_directory`, but only on
   * its OWN row — which is the point of it: null here is how the SPA knows to print the `/link`
   * instructions instead of an account badge. It cannot reach the other two surfaces at all.
   *
   * Null-not-absent: the key is always present, which is what lets this be `string | null` rather
   * than optional, and null is the signal the SPA reads to offer `/link` instructions.
   */
  readonly discord_id: string | null;
}

/**
 * An item spec as the database stores it - the JSON twin of `business`'s `ItemSpec` value
 * and of the authored `ItemSpecDef` config block, mirroring `:database`'s `ItemSpecPayload`
 * key for key.
 *
 * `nbt` is an opaque SNBT blob contributing COMPONENTS ONLY: `itemId` and `count` are
 * authoritative for identity and quantity, and if the blob disagrees it loses. The browser
 * cannot parse SNBT, so this precedence is what lets the editor retype an item id and have
 * it mean something. `nbt` is always present as a key (the mapper writes nulls rather than
 * omitting them), hence `string | null` and not an optional property.
 */
export interface ItemSpecDocument {
  readonly itemId: string;
  readonly count: number;
  readonly nbt: string | null;
}

/**
 * `web.player_pending_rewards` - the reward mailbox.
 *
 * `payload` stays `unknown` on purpose: it is the RewardPayload discriminated union, which
 * the browser renders generically from the generated JSON Schema rather than from a
 * hand-written TS mirror that would rot the moment a payload kind is added.
 *
 * `display_item` is an item spec DOCUMENT, not a string, as of migration 1.6. It used to be
 * a whole-stack SNBT blob typed `text`; 1.6 made the column `json` (verified on a real PG16:
 * `information_schema.columns` reads `json` for both `pending_reward.display_item` and this
 * view's copy of it), so PostgREST emits an object. It is the mailbox "phenotype" icon and is
 * explicitly allowed to differ from `payload`. Null means no custom icon - the menu falls
 * back to its default. Unlike `payload` this one IS mirrored, because it is a fixed
 * three-field shape rather than an open union.
 */
export interface PlayerPendingRewardRow extends WebPlayerRef {
  readonly id: number;
  readonly description: string | null;
  readonly display_item: ItemSpecDocument | null;
  readonly payload: unknown;
  readonly created_at: IsoInstant;
}

/**
 * `web_self.player_tags` - the cosmetic prefix/title tags this player has unlocked, and which
 * one is equipped.
 *
 * SELF-SERVICE ONLY, AND THAT IS WHY IT IS NOT IN {@link WEB_VIEW_NAMES}. Unlike the ten views
 * above there is no `web.player_tags` staff twin: a player's unlocks are their own business (R2),
 * so the view exists only in the `web_self` schema, created by
 * `web/deploy/postgres-init/web-roles.sql` section 5 and reachable only under a `player` token.
 * It is therefore absent from {@link PLAYER_VIEW_NAMES} too, which is the both-schemas set the
 * catalogue comparison walks.
 *
 * NO `last_known_name`, WHICH BREAKS THE THREE-LEADING-COLUMNS CONVENTION ON PURPOSE. That
 * convention exists so a staff view can render a row with the subject's name without a second
 * lookup; this view has no staff reader and the subject is always the caller, so the column would
 * be ceremony. `player_uuid` and `server_id` still lead, for keying.
 *
 * `equipped` is derived in SQL as `tag_id = player_profile.selected_tag_id` (the one equipped tag
 * is a column on the identity row, not a second table), so exactly zero or one row per player is
 * `equipped = true`. It is `boolean | null`, not `boolean`, and the null is three-valued logic
 * showing through rather than a modelling choice: when a player has unlocked tags but equips none,
 * `selected_tag_id` is null and `tag_id = null` is `null` on every row — PostgREST renders that as
 * JSON `null`. The predicate a reader wants is `row.equipped === true` (which excludes both null
 * and false); `!row.equipped` also reads correctly as "not the equipped one". `tag_id` is the raw
 * config key `[a-z0-9_-]{1,32}`; resolving it to a display prefix is the config's job, not this
 * row's.
 */
export interface PlayerTagRow {
  readonly player_uuid: UuidText;
  readonly server_id: string;
  readonly tag_id: string;
  readonly equipped: boolean | null;
  readonly created_at: IsoInstant;
}

/** `web.player_redemptions` - mystery gifts already claimed, resolved to the human gift code. */
export interface PlayerRedemptionRow extends WebPlayerRef {
  readonly id: number;
  readonly code: string;
  readonly redeemed_at: IsoInstant;
}

/** `web.player_homes` */
export interface PlayerHomeRow extends WebPlayerRef {
  readonly id: number;
  readonly name: string;
  readonly dimension: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly pitch: number;
  readonly created_at: IsoInstant;
}

/** `web.player_teleport_prefs` */
export interface PlayerTeleportPrefRow extends WebPlayerRef {
  readonly id: number;
  readonly tpa_blocked: boolean;
}

/**
 * `web.player_secret_bases` - a player's own bases.
 *
 * `is_default` / `is_logout_placement` are folded in from the two single-row pointer
 * tables, narrowed to the owner's own row. `is_logout_placement` is therefore the cheap
 * common case only; the full picture (logout in someone else's base, or eject-pending with
 * no base at all) is {@link PlayerLogoutPlacementRow}.
 */
export interface PlayerSecretBaseRow extends WebPlayerRef {
  readonly id: number;
  readonly base_uuid: UuidText;
  readonly name: string;
  readonly status: string;
  readonly size_level: number;
  readonly template_key: string;
  readonly locked: boolean;
  readonly public_flag: boolean;
  readonly border_visible: boolean;
  readonly biome_key: string | null;
  readonly weather_key: string | null;
  readonly time_key: string | null;
  readonly last_entered_at: IsoInstant | null;
  readonly created_at: IsoInstant;
  readonly is_default: boolean;
  readonly is_logout_placement: boolean;
}

/** `web.player_base_trusts` - bases owned by OTHER players that trust this one. */
export interface PlayerBaseTrustRow extends WebPlayerRef {
  readonly id: number;
  readonly base_uuid: UuidText;
  readonly base_name: string;
  readonly base_status: string;
  readonly base_owner_uuid: UuidText;
  readonly base_owner_name: string | null;
}

/**
 * `web.player_logout_placement` - at most one row per player.
 *
 * All four `base_*` columns null is exactly the eject-pending state (the base was deleted
 * and the FK's ON DELETE SET NULL left the row behind saying "eject me on join"). A
 * `base_owner_uuid` other than `player_uuid` is exactly the logged-out-in-a-stranger's-base
 * state. Neither is visible through `is_logout_placement` on the bases view.
 */
export interface PlayerLogoutPlacementRow extends WebPlayerRef {
  readonly id: number;
  readonly base_uuid: UuidText | null;
  readonly base_name: string | null;
  readonly base_owner_uuid: UuidText | null;
  readonly base_owner_name: string | null;
}

/**
 * `web.outbox_status` - what the web asked for, and what this server did about it.
 *
 * THE ONLY WAY THE BROWSER LEARNS THE OUTCOME OF A GRANT. The write path files a PENDING
 * intent and is told nothing back but "filed" (`GrantAccepted` carries no row id, because
 * the write role holds no SELECT on `web_outbox` - not even on the row it just wrote). The
 * issuing page finds its own row by reading this view with a READ token, which is the only
 * direction data is allowed to flow.
 *
 * `server_id` IS A REAL COLUMN here, not 1.4's stamped literal: an intent is claimed by
 * exactly one server. See 1.8's header - this view is where that swap happened first.
 *
 * `payload` is a `WebIntentPayload` document - `{reward, description, displayItem}` - and
 * NOT a bare `RewardPayload`; the reward is one field inside it. Left `unknown` for the same
 * reason the mailbox's is: the reward is an open union rendered generically from the
 * generated schema, and a hand-written TS mirror of it would rot on the next reward kind.
 *
 * `server_heartbeat_at` / `server_booted_at` come from the LEFT JOIN onto `server_status`
 * and are null when no server has ever beaten under this `server_id` - a typo in
 * `editor.serverId`, or a server not yet booted. That is a state worth seeing: an intent
 * nobody will ever claim looks exactly like a fresh one without it. There is deliberately
 * no `is_online` column; the reader compares the heartbeat to its own clock.
 */
export interface OutboxStatusRow extends WebPlayerRef {
  readonly id: number;
  /** Who asked, as the Worker's JWT knew them: `discord:<snowflake> (<name>)`. */
  readonly actor: string;
  /** Today always `reward.grant`; the mod refuses an action it does not recognise by name. */
  readonly action: string;
  /** One of {@link OUTBOX_STATUSES}. `pending` and `claimed` are in flight; the rest are terminal. */
  readonly status: string;
  readonly payload: unknown;
  /** When the intent becomes claimable. Null means "as soon as the server picks it up". */
  readonly execute_at: IsoInstant | null;
  readonly created_at: IsoInstant;
  /** When the mod settled it, done or failed. Null while it is still in flight. */
  readonly settled_at: IsoInstant | null;
  /** The mod's own words for a refusal. Null unless `status` is `failed`. */
  readonly failure_reason: string | null;
  readonly server_heartbeat_at: IsoInstant | null;
  readonly server_booted_at: IsoInstant | null;
}

/**
 * `web.audit_log` - what the mod actually did, after the fact.
 *
 * The one surface in the read plane that cannot be shaped by whoever is reading it: the web
 * holds no privilege at all on `web_audit`, not even INSERT. A FAILED intent leaves no row
 * here, and a row here has no foreign key back to the intent that caused it - the audit
 * outlives a pruned outbox, so the four facts are copies rather than a join that would one
 * day dangle.
 */
export interface AuditLogRow extends WebPlayerRef {
  readonly id: number;
  readonly actor: string;
  readonly action: string;
  readonly payload: unknown;
  /** When the mod applied it. The column is `at`, not `created_at`; the audit is the event. */
  readonly at: IsoInstant;
}

/**
 * `web.audit_event` - the audit log (migration 1.38): every command a person ran and every staff action the
 * modules recorded, the same records `/audit` reads in game (docs/modules/audit.md).
 *
 * STAFF-GLOBAL, LIKE {@link AuditLogRow}: read through `web_read` only. `web_player` cannot name the schema, and
 * there is no `web_self` twin (owner ruling 2026-11-01, docs/design/README.md decision 1).
 *
 * The leading identity is the record's TARGET, the player it was done to, and is null for a record with none
 * (the command log's rows, a server-wide change) - which is why `player_uuid` is nullable here, unlike on every
 * per-player view. The actor has its own pair: `actor_name` is the name the record was written with (the
 * console and the server's own parts have one but no account), `actor_last_known_name` the account's name now.
 */
export interface AuditEventRow extends Omit<WebPlayerRef, 'player_uuid'> {
  readonly player_uuid: UuidText | null;
  readonly id: number;
  /** Dotted, lower case: `reward.give`, `command.tp`. */
  readonly action: string;
  /** The action's first segment, which is what `/audit module:<id>` filters on. */
  readonly module: string;
  /** `PLAYER`, `CONSOLE`, `WEB` or `SYSTEM`. */
  readonly actor_kind: string;
  readonly actor_name: string;
  readonly actor_uuid: UuidText | null;
  readonly actor_last_known_name: string | null;
  readonly summary: string;
  /** Where it happened, when a place belongs to it; all four are null otherwise. */
  readonly dimension: string | null;
  readonly x: number | null;
  readonly y: number | null;
  readonly z: number | null;
  readonly created_at: IsoInstant;
}

/**
 * `web.cobblemon_ledger` - which Cobblemon changed hands, and how (migration 1.38; docs/modules/cobblemonledger.md),
 * keyed by the entry's owner. Staff-global like {@link AuditEventRow}.
 *
 * Every column of the table but the snapshot: the saved Cobblemon is a restore's input, and a restore is a game-side
 * action the web never takes. `action` is `trade_out`, `trade_in`, `release`, `gift_out`, `gift_in`, `clearbox`,
 * `specialpoke_give`, `wondertrade_out`, `wondertrade_in` or `restore`.
 */
export interface CobblemonLedgerRow extends WebPlayerRef {
  readonly id: number;
  readonly action: string;
  /** Namespaced: `cobblemon:eevee`. */
  readonly species: string;
  readonly level: number;
  readonly shiny: boolean;
  readonly aspects: string;
  readonly cobblemon_uuid: UuidText;
  readonly counterparty_uuid: UuidText | null;
  readonly counterparty_last_known_name: string | null;
  readonly note: string | null;
  readonly created_at: IsoInstant;
  /** When a staff member gave this entry's Cobblemon back; null until then. */
  readonly restored_at: IsoInstant | null;
}

/**
 * `web.player_report` - the report queue (migration 1.38; `/report` and `/reports`, docs/modules/chat.md), keyed by
 * the player who filed it. Staff-global like {@link AuditEventRow}, and named for its table rather than for the
 * per-player family: it is the one `web.player_*` view with no `web_self` twin, on purpose.
 */
export interface PlayerReportRow extends WebPlayerRef {
  readonly id: number;
  /** `OPEN`, `CLAIMED` or `CLOSED`. */
  readonly status: string;
  /** The reporter's own words. */
  readonly reason: string;
  /** Null for a help request, which names nobody. */
  readonly reported_uuid: UuidText | null;
  readonly reported_last_known_name: string | null;
  /** The staff member who claimed or closed it; null until somebody does. */
  readonly handler_uuid: UuidText | null;
  readonly handler_last_known_name: string | null;
  readonly note: string | null;
  readonly dimension: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly created_at: IsoInstant;
  readonly closed_at: IsoInstant | null;
}

/**
 * `web.player_tower_runs` / `web_self.player_tower_runs` - every finished Battle Tower run (migration 1.23), keyed
 * by its climber. One row per run, whatever ended it: a save at a checkpoint is a run too, and the climb that
 * resumes from it is the next row, starting at that `start_score`.
 *
 * NOT YET IN {@link WEB_VIEW_NAMES} OR {@link PLAYER_VIEW_NAMES}: the SQL, the grants and the RLS ship with the
 * Tower Records (dev-stack.sh covers both schemas), and the sidecar's allow-list gains the name with the phone's
 * Tower app, together with a panel to render it. `daily_seed` is deliberately absent from both schemas.
 *
 * `mode` is `NORMAL` or `DAILY` (then `daily_date` is set); `outcome` is one of `LOST`, `FORFEITED`, `SAVED`,
 * `EXITED`, `LEFT`, `REMOVED`, `FAILED`, `SHUTDOWN`. `season_start`/`season_end` are the leaderboard season the
 * run counts towards, stamped by the mod when the run ended (a daily run counts towards its date's season).
 */
export interface PlayerTowerRunRow extends WebPlayerRef {
  readonly id: number;
  readonly tower_id: string;
  readonly mode: string;
  readonly daily_date: string | null;
  readonly start_score: number;
  readonly score: number;
  readonly outcome: string;
  readonly started_at: IsoInstant;
  readonly ended_at: IsoInstant;
  readonly season_start: IsoInstant;
  readonly season_end: IsoInstant;
  readonly beat_champions: boolean;
}

/**
 * `web.tower_leaderboard` / `web_self.tower_leaderboard` - the Battle Tower's leaderboard (migration 1.23): one row
 * per player and board, a board being a tower, a mode, a season and - for the daily tower - a daily date. The row
 * carries the player's best score there, when they first reached it, how many runs they made and their `rank`
 * (best first, the earlier on a tie). "This week" is the board whose `season_start <= now < season_end`; "today's
 * daily tower" is `mode = DAILY` and `daily_date` = today. Old seasons stay: nothing is deleted at a reset.
 *
 * PUBLIC on the player tier, unlike every other `web_self` view: it is the same board that floats in the tower's
 * lobby, read through a definer keyhole that returns these columns and nothing of a raw run (web-roles.sql 5c).
 * Not yet allow-listed by the sidecar, for the same reason as {@link PlayerTowerRunRow}.
 */
export interface TowerLeaderboardRow extends WebPlayerRef {
  readonly tower_id: string;
  readonly mode: string;
  readonly season_start: IsoInstant;
  readonly season_end: IsoInstant;
  readonly daily_date: string | null;
  readonly best_score: number;
  readonly reached_at: IsoInstant;
  readonly runs: number;
  readonly rank: number;
}

/**
 * Every view name the read plane exposes, as PostgREST paths.
 *
 * PostgREST serves the `web` schema as its root, so a view is reached at `/<name>`; the
 * leading segment is not repeated. Keep this list exhaustive - the inspector iterates it,
 * and the sidecar's proxy allow-lists against it, so a view missing from this array is a
 * view the browser gets a 404 for however well it exists in Postgres.
 */
export const WEB_VIEW_NAMES = [
  'player_directory',
  'player_pending_rewards',
  'player_redemptions',
  'player_homes',
  'player_teleport_prefs',
  'player_secret_bases',
  'player_base_trusts',
  'player_logout_placement',
  'outbox_status',
  'audit_log',
  'audit_event',
  'cobblemon_ledger',
  'player_report',
] as const;

export type WebViewName = (typeof WEB_VIEW_NAMES)[number];

/**
 * The staff-global audit surfaces of migration 1.38, which the Audit page renders as tabs beside `audit_log`.
 *
 * Named apart because they are SERVER-WIDE records rather than a player's collection: each row names a player,
 * but the per-player inspector does not fan out over them, so `/player/:uuid` excludes them by this list rather
 * than growing three cards nobody asked for. Every one is staff-only, so each is in
 * {@link STAFF_ONLY_VIEW_NAMES} too.
 */
export const AUDIT_VIEW_NAMES = ['audit_event', 'cobblemon_ledger', 'player_report'] as const satisfies readonly WebViewName[];

export type AuditViewName = (typeof AUDIT_VIEW_NAMES)[number];

/**
 * The eight views that exist in BOTH schemas: `web.<name>` for staff, unfiltered, and
 * `web_self.<name>` for a `player` token, filtered by Postgres to the caller's own `uuid`
 * claim. Same names, same columns, same row types above — which is why one set of interfaces
 * serves both and why the SPA needs no branch on the tier to render a row.
 *
 * `web_self`'s copies are created by `web/deploy/postgres-init/web-roles.sql` section 5 rather
 * than by a migration (security_invoker, RLS and role names are Postgres-only, and the mod's
 * migration chain has to apply on H2 too). dev-stack.sh compares the two catalogues column by
 * column and ordinal by ordinal on every run, so a change to one that is not mirrored in the
 * other fails the gate rather than the SPA.
 */
export const PLAYER_VIEW_NAMES = [
  'player_directory',
  'player_pending_rewards',
  'player_redemptions',
  'player_homes',
  'player_teleport_prefs',
  'player_secret_bases',
  'player_base_trusts',
  'player_logout_placement',
] as const;

export type PlayerViewName = (typeof PLAYER_VIEW_NAMES)[number];

/**
 * The views a player has no business in, and the reason `PLAYER_VIEW_NAMES` is a list
 * rather than a filter on the prefix (`player_report` begins with it and is one of them).
 *
 * `audit_log` is what staff did on this server's behalf and `outbox_status` carries every
 * actor's Discord snowflake plus every spent `/link` code, permanently (the outbox is settled,
 * never pruned). The three {@link AUDIT_VIEW_NAMES} are the audit log, the Cobblemon ledger and
 * the report queue: records about players kept for staff. None is per-player and none could be
 * made per-player by adding a filter: they are a different subject.
 *
 * THIS ARRAY IS NOT THE ENFORCEMENT. `web_player` holds no usage on schema `web`, so both are
 * unreachable from a player-tier request whatever the sidecar allows; the split exists so the
 * refusal is a 403 that says which surface was asked for instead of a laundered Postgres
 * error. Derived by subtraction so a ninth player view cannot be forgotten here.
 */
export const STAFF_ONLY_VIEW_NAMES = WEB_VIEW_NAMES.filter(
  (name) => !(PLAYER_VIEW_NAMES as readonly string[]).includes(name),
) as readonly WebViewName[];

export function isPlayerViewName(value: string): value is PlayerViewName {
  return (PLAYER_VIEW_NAMES as readonly string[]).includes(value);
}

/** Compile-time map from view name to row type. Purely a type; there is no runtime value. */
export interface WebViewRowMap {
  player_directory: PlayerDirectoryRow;
  player_pending_rewards: PlayerPendingRewardRow;
  player_redemptions: PlayerRedemptionRow;
  player_homes: PlayerHomeRow;
  player_teleport_prefs: PlayerTeleportPrefRow;
  player_secret_bases: PlayerSecretBaseRow;
  player_base_trusts: PlayerBaseTrustRow;
  player_logout_placement: PlayerLogoutPlacementRow;
  outbox_status: OutboxStatusRow;
  audit_log: AuditLogRow;
  audit_event: AuditEventRow;
  cobblemon_ledger: CobblemonLedgerRow;
  player_report: PlayerReportRow;
}

/** Row type for a given view name. */
export type WebViewRow<N extends WebViewName> = WebViewRowMap[N];
